import React from 'react'
import { Box, Button, CircularProgress, Tooltip } from '@mui/material'
import { Icon } from 'components'
import { useTranslation } from 'react-i18next'
import { DataType } from 'types'

interface ParticipantActionButtonsProps {
  isMobile: boolean
  selectedEmails: string[]
  selectedRowsIds: number[]
  isAssigning: boolean
  isUnassigning: boolean  
  contentType: DataType
  licenseStatusOfSelection: 'assigned' | 'unassigned' | 'mixed' | null
  handleSendEmail: () => void
  handleUnassignUsers: () => void
  handleAssignUsers: () => void
}

const ParticipantActionButtons: React.FC<ParticipantActionButtonsProps> = ({
  isMobile,
  selectedRowsIds,
  isAssigning,
  isUnassigning,
  contentType,
  licenseStatusOfSelection,
  handleSendEmail,
  handleUnassignUsers,
  handleAssignUsers,
}) => {
  const { t } = useTranslation()

  const buttonSize = isMobile
    ? {
        minWidth: 150,
        padding: '0.5rem 1.5rem',
      }
    : {
        minWidth: 150,
      }

  const hasSelection = selectedRowsIds.length > 0
  const isProcessing = isAssigning || isUnassigning

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      flexDirection={isMobile ? 'column' : 'row'}
      alignItems='center'
      mb={4}
      gap={2}
    >
      {/* Send Message Button */}
      <Tooltip
        title={!hasSelection || isProcessing ? t('select_user_first') : ''}
      >
        <span>
          <Button
            color='secondary'
            variant='outlined'
            size='small'
            disabled={!hasSelection || isProcessing}
            onClick={handleSendEmail}
            sx={buttonSize}
          >
            {t('send_message')}
          </Button>
        </span>
      </Tooltip>

      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        gap={2}
      >
        {/* Unassign Button */}
        <Tooltip
          title={
            !hasSelection
              ? t('select_user_first')
              : licenseStatusOfSelection === 'mixed'
              ? t('mixed_users_cannot_be_unassigned')
              : ''
          }
        >
          <span>
            <Button
              variant='contained'
              color='secondary'
              startIcon={
                isUnassigning ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  <Icon iconId='remove' />
                )
              }
              onClick={handleUnassignUsers}
              disabled={
                !hasSelection ||
                isProcessing ||
                licenseStatusOfSelection !== 'assigned' ||
                contentType !== DataType.Program
              }
              sx={buttonSize}
            >
              {t('unassign_license')}
            </Button>
          </span>
        </Tooltip>

        <Tooltip
          title={
            !hasSelection
              ? t('select_user_first')
              : licenseStatusOfSelection === 'mixed'
              ? t('mixed_users_cannot_be_assigned')
              : ''
          }
        >
          <span>
            <Button
              variant='contained'
              size='small'
              color='primary'
              startIcon={
                isAssigning ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  <Icon iconId='add' />
                )
              }
              onClick={handleAssignUsers}
              disabled={
                !hasSelection ||
                isProcessing ||
                licenseStatusOfSelection !== 'unassigned'
              }
              sx={buttonSize}
            >
              {t('assign_license')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default ParticipantActionButtons
